import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SafeLoading } from "@/components/ui/safe-loading";
import { isEventCheckoutComplete } from "@/utils/eventPaymentStatus";

// Pattern for short share codes: lowercase letters, digits and hyphens (3-40 chars).
// Hyphens are allowed so links can carry a readable name (ex.: /veiga-bbq), not only
// random codes (ex.: /0l8jhz). Starts and ends with a letter/digit so it never matches
// "/" or a path that is clearly not a share code.
const SHORT_CODE_PATTERN = /^\/[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?$/;

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [resolvingShortLink, setResolvingShortLink] = useState(false);

  useEffect(() => {
    // Try to resolve as a short share link before showing 404
    const tryResolveShortLink = async () => {
      const path = location.pathname;
      if (!SHORT_CODE_PATTERN.test(path)) return false;

      const code = path.slice(1); // remove leading "/"
      setResolvingShortLink(true);

      try {
        const { data, error } = await supabase
          .from('store_share_links')
          .select('supplier_slug, params')
          .eq('code', code)
          .maybeSingle();

        if (!error && data) {
          const params = (data.params as Record<string, string>) || {};
          
          // Check if pid is present and if it's already finished/paid
          const pid = params.pid;
          if (pid) {
            const { data: event } = await supabase
              .from('events')
              .select('status_payments, status')
              .eq('id', pid)
              .maybeSingle();
            
            const isFinished = isEventCheckoutComplete(event?.status_payments, event?.status);
            
            if (isFinished) {
              // If already paid or finished, redirect with expired flag
              navigate(`/loja/${data.supplier_slug}?expired=paid`, { replace: true });
              return true;
            }
          }

          const qs = new URLSearchParams(params).toString();
          const target = `/loja/${data.supplier_slug}${qs ? '?' + qs : ''}`;
          navigate(target, { replace: true });
          return true;
        }
      } catch (e) {
        console.warn('Short link resolution failed:', e);
      }

      setResolvingShortLink(false);
      return false;
    };

    tryResolveShortLink().then((resolved) => {
      if (resolved) return;

      // Enhanced 404 logging for monitoring
      const error404Data = {
        path: location.pathname,
        search: location.search,
        hash: location.hash,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      };

      console.error("404 Error: User attempted to access non-existent route:", error404Data);

      if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('event', '404_error', {
          page_path: location.pathname,
          referrer: document.referrer
        });
      }
    });
  }, [location.pathname, location.search, location.hash, navigate]);

  if (resolvingShortLink) {
    return <SafeLoading size="lg" message="Redirecionando..." />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-4">404</h1>
        <p className="text-xl text-gray-600 mb-4">Oops! Page not found</p>
        <a href="/" className="text-blue-500 hover:text-blue-700 underline">
          Return to Home
        </a>
      </div>
    </div>
  );
};

export default NotFound;
